import { useState, useRef, useEffect, RefObject } from 'react';

interface ZoomConfig {
    minHeight: number;
    maxHeight: number;
    sensitivity: number;
    anchorTime?: number; // Decimal hours (e.g. 14.5 for 14:30)
}

/**
 * useTimelineZoom: Multi-input gesture engine for pinned timeline scaling.
 * HARDENED: Optimized scroll syncing to mitigate scroll-linked positioning warnings.
 * ENHANCED: Optional anchorTime centering (e.g. for centering on the "Now" line).
 */
export const useTimelineZoom = (
    containerRef: RefObject<HTMLDivElement | null>,
    initialHeight: number,
    config: ZoomConfig = { minHeight: 20, maxHeight: 600, sensitivity: 2.5 }
) => {
    const [hourHeight, setHourHeight] = useState(initialHeight);
    const [isZooming, setIsZooming] = useState(false);

    const state = useRef({
        startY: 0,
        startDistance: 0,
        startHeight: 0,
        pinTime: 0, // The hour coordinate we grabbed
        currentLocalY: 0, // The visual Y where the finger is NOW
        rafId: null as number | null
    });

    // EFFECT: Manage touch-action to prevent browser interference
    useEffect(() => {
        const container = containerRef.current;
        if (!container) return;

        if (isZooming) {
            container.style.touchAction = 'none';
            container.style.overflowAnchor = 'none';
        } else {
            container.style.touchAction = 'pan-y';
            container.style.overflowAnchor = 'auto';
        }
    }, [isZooming, containerRef]);

    // PRECISION PINNING: Sync scroll position with zoom level.
    useEffect(() => {
        if (isZooming && containerRef.current) {
            const { pinTime, currentLocalY } = state.current;
            const targetScroll = (pinTime * hourHeight) - currentLocalY;
            containerRef.current.scrollTop = targetScroll;
        }
    }, [hourHeight, isZooming, containerRef]);

    const handleTouchStart = (e: React.TouchEvent | React.MouseEvent) => {
        const container = containerRef.current;
        if (!container) return;

        let clientY = 0;
        let distance = 0;
        const rect = container.getBoundingClientRect();

        if ('touches' in e) {
            if (e.touches.length === 2) {
                const t1 = e.touches[0];
                const t2 = e.touches[1];
                clientY = (t1.clientY + t2.clientY) / 2;
                distance = Math.sqrt(Math.pow(t2.clientX - t1.clientX, 2) + Math.pow(t2.clientY - t1.clientY, 2));
            } else if (e.touches.length === 1) {
                const touchX = e.touches[0].clientX - rect.left;
                if (touchX > 60) return; 
                clientY = e.touches[0].clientY;
            } else return;
        } else {
            const mouseX = (e as React.MouseEvent).clientX - rect.left;
            if (mouseX > 60) return;
            clientY = (e as React.MouseEvent).clientY;
        }

        const localY = clientY - rect.top;

        setIsZooming(true);
        state.current.startY = clientY;
        state.current.currentLocalY = (config.anchorTime !== undefined) ? (container.offsetHeight / 2) : localY;
        state.current.startDistance = distance;
        state.current.startHeight = hourHeight;
        
        // If anchorTime is provided, we lock to that time. 
        // Otherwise, we lock to the time under the finger.
        state.current.pinTime = (config.anchorTime !== undefined) 
            ? config.anchorTime 
            : (localY + container.scrollTop) / hourHeight;
    };

    useEffect(() => {
        if (!isZooming) return;

        const handleMove = (e: MouseEvent | TouchEvent) => {
            const container = containerRef.current;
            if (!container) return;

            if (e.cancelable) e.preventDefault();

            let clientY = 0;
            let distance = 0;
            let isPinch = false;

            if ('touches' in e) {
                if (e.touches.length === 2) {
                    isPinch = true;
                    const t1 = e.touches[0];
                    const t2 = e.touches[1];
                    clientY = (t1.clientY + t2.clientY) / 2;
                    distance = Math.sqrt(Math.pow(t2.clientX - t1.clientX, 2) + Math.pow(t2.clientY - t1.clientY, 2));
                } else {
                    clientY = e.touches[0].clientY;
                }
            } else {
                clientY = (e as MouseEvent).clientY;
            }

            let newHeight = state.current.startHeight;
            if (isPinch && state.current.startDistance > 0) {
                const ratio = distance / state.current.startDistance;
                newHeight = state.current.startHeight * ratio;
            } else {
                const deltaY = clientY - state.current.startY;
                newHeight = state.current.startHeight - (deltaY / config.sensitivity);
            }

            setHourHeight(Math.max(config.minHeight, Math.min(config.maxHeight, newHeight)));
        };

        const handleEnd = () => setIsZooming(false);

        window.addEventListener('mousemove', handleMove);
        window.addEventListener('mouseup', handleEnd);
        window.addEventListener('touchmove', handleMove, { passive: false });
        window.addEventListener('touchend', handleEnd);

        return () => {
            window.removeEventListener('mousemove', handleMove);
            window.removeEventListener('mouseup', handleEnd);
            window.removeEventListener('touchmove', handleMove);
            window.removeEventListener('touchend', handleEnd);
        };
    }, [isZooming, config, containerRef]);

    return {
        hourHeight,
        setHourHeight,
        isZooming,
        handleZoomStart: (e: any) => handleTouchStart(e)
    };
};
